import { useCallback, useMemo, useRef, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  closeSupportChat,
  customerHref,
  errorMessage,
  fetchAppointments,
  fetchCareTasks,
  fetchClinicHealth,
  fetchExpiryTasks,
  fetchPendingChats,
  fetchPendingCounts,
  fetchPendingSupportChats,
  fetchRecentlyAnswered,
  fetchWaSessions,
  markTaskDone,
  snoozeAppointment,
  snoozeConversation,
  snoozeTask,
  type CloseSupportChatResponse,
  type EditTaskResponse,
  type PendingSupportChat,
  type SnoozeAppointmentResponse,
  type SnoozeConversationResponse,
  type SnoozeDuration,
  type TaskStatusResponse,
} from "./api";
import {
  buildAppointments,
  buildCareWaiting,
  buildP0,
  buildP2,
  buildSnoozedRows,
  describeQuietP0,
  formatWaiting,
  summarize,
  summarizeQuestionCounts,
  type CareWaitingItem,
  type SnoozedRowData,
  type WorklistItem,
} from "./priority";
import CareWaitingCard from "./CareWaitingCard";
import ClinicHealthStrip, {
  ConnectionsPill,
  healthHasAlert,
} from "./ClinicHealthStrip";
import Customer360 from "./Customer360";
import Pulse from "./Pulse";
import RetentionStrip from "./RetentionStrip";
import SupportChatCard from "./SupportChatCard";
import TierSection from "./TierSection";
import WorklistCard from "./WorklistCard";

/**
 * Card actions are DELIBERATELY side-effect free: navigate, or change internal state
 * in picard's DB. Nothing here can send a WhatsApp message, an SMS, a push or a call.
 * The P2 "Message on WhatsApp" action is a LINK into waplify's chat UI — a human
 * writes and sends the message there. We do not build auto-outreach.
 */
type ActionVars =
  | { type: "mark-done"; item: WorklistItem }
  | { type: "snooze"; item: WorklistItem; duration: SnoozeDuration };

type ActionResult =
  | TaskStatusResponse
  | EditTaskResponse
  | SnoozeConversationResponse
  | SnoozeAppointmentResponse;

/**
 * Row-error key for an escalating support chat. The `errors` map is shared with the
 * WorklistItem/CareWaitingItem rows, which carry their own `key`; a support chat has only
 * a numeric id, so namespace it rather than risk colliding with those.
 */
const supportChatKey = (chatId: number) => `support-chat:${chatId}`;

/* ---------- URL state ---------- */
// The queue's view state lives in the URL, so a CM can bookmark "my P0 view", share
// "look at these three expiring customers" in Slack, and get Back/Forward for free.

/* ---------- states ---------- */

// States render as ROWS inside the tier panel (TierSection owns the border).

function Notice({ children }: { children: React.ReactNode }) {
  return (
    <li
      data-testid="worklist-empty"
      className="px-5 py-5 text-center text-sm text-clinic-500 dark:text-gray-400"
    >
      {children}
    </li>
  );
}

/** Loading is a skeleton, not a spinner — it shows the SHAPE of what's coming. */
function LoadingTier() {
  return (
    <>
      <li className="sr-only" role="status">
        Loading…
      </li>
      {[0, 1].map((i) => (
        <li
          key={i}
          aria-hidden="true"
          className="flex items-center gap-4 border-l-4 border-clinic-200 px-4 py-2.5 dark:border-navy-700"
        >
          <div className="h-8 w-14 shrink-0 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
            <div className="h-3 w-1/4 animate-pulse rounded bg-clinic-100 motion-reduce:animate-none dark:bg-navy-700" />
          </div>
        </li>
      ))}
    </>
  );
}

/** An error the CM can act on: what failed, and the one button that fixes it. */
function ErrorTier({ onRetry }: { onRetry: () => void }) {
  return (
    <li role="alert" className="px-5 py-5 text-center">
      <p className="text-sm font-medium text-clinic-900 dark:text-white">
        Couldn&rsquo;t load this queue.
      </p>
      <p className="mt-1 text-xs text-clinic-500 dark:text-gray-400">
        Usually a dropped connection. Retry — if it keeps failing, check that
        picard is reachable.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 touch-manipulation rounded-md bg-action-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-action-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 motion-reduce:transition-none"
      >
        Retry
      </button>
    </li>
  );
}

/* ---------- P0 segments (inside the ONE merged panel) ---------- */

/**
 * The three P0 streams are one queue in a CM's head, so they live in ONE panel under one
 * ACT NOW tag — three separate bordered panels meant three competing alarms, and red
 * stopped meaning anything. Each segment is a hairline divider row; an EMPTY segment
 * collapses to just that row with its reason inline, instead of a full empty box.
 *
 * We deliberately do NOT interleave across segments: the streams rank on different
 * scales and their cards carry different actions (link-only / dismiss+draft /
 * snooze+360). Honest within-segment order under one border beats a fake merge.
 */
function Segment({
  id,
  label,
  count,
  quiet,
  testId,
}: {
  id: string;
  label: string;
  count: number;
  /** why it's empty — shown inline so a quiet segment costs one row, not a panel */
  quiet?: string;
  testId: string;
}) {
  return (
    <li
      id={id}
      data-testid={testId}
      data-count={count}
      className="flex scroll-mt-4 items-center gap-2 bg-clinic-50 px-4 py-1.5 dark:!bg-navy-900"
    >
      <span className="whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400">
        {label}
      </span>
      <span
        className={`rounded px-1 font-mono text-[10px] font-semibold tabular-nums ${
          count > 0
            ? "bg-crit-600 text-white"
            : "bg-clinic-200 text-clinic-600 dark:bg-navy-700 dark:text-gray-300"
        }`}
      >
        {count}
      </span>
      {count === 0 && quiet && (
        <span className="truncate text-[11px] text-clinic-500 dark:text-gray-400">
          {quiet}
        </span>
      )}
      <hr className="flex-1 border-clinic-200 dark:border-navy-700" />
    </li>
  );
}

/* ---------- summary header ---------- */

/**
 * The strip's colour grammar: neutral = information, warn/crit = a breach figure
 * that is nonzero. A plain count ("4 questions pending") must never shout.
 */
type StatTone = "default" | "warn" | "critical";

const statTone: Record<StatTone, string> = {
  default: "text-clinic-900 dark:text-white",
  warn: "text-warn-600 dark:text-amber-400",
  critical: "text-crit-600 dark:text-red-400",
};

const subTone: Record<StatTone, string> = {
  default: "text-clinic-500 dark:text-gray-400",
  warn: "text-warn-600 dark:text-amber-400",
  critical: "text-crit-600 dark:text-red-400",
};

/**
 * Console gauge: big tabular value over a tracked uppercase micro-label, with an
 * optional small sub-figure ("2 past SLA", "1 snoozed") — the second-order fact
 * that turns a bare count into situational awareness.
 */
function Stat({
  value,
  label,
  sub,
  tone = "default",
  subToneName = "default",
  testId,
  href,
}: {
  value: string;
  label: string;
  sub?: string;
  tone?: StatTone;
  subToneName?: StatTone;
  testId?: string;
  /** anchor to the lane segment this tile counts — the glance IS the table of contents */
  href?: string;
}) {
  const body = (
    <>
      <div
        className={`text-[22px] font-bold tabular-nums leading-none ${statTone[tone]}`}
      >
        {value}
      </div>
      <div className="mt-1.5 whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400">
        {label}
      </div>
      {sub && (
        <div
          className={`mt-1 whitespace-nowrap text-[11px] font-medium tabular-nums leading-none ${subTone[subToneName]}`}
        >
          {sub}
        </div>
      )}
    </>
  );

  const cls = "min-w-0 flex-1 px-5 py-3 first:pl-5";
  return href ? (
    <a
      data-testid={testId}
      href={href}
      className={`${cls} block transition-colors hover:bg-clinic-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action-500 dark:hover:!bg-navy-700`}
    >
      {body}
    </a>
  ) : (
    <div data-testid={testId} className={cls}>
      {body}
    </div>
  );
}

/* ---------- snoozed rows (revealed by ?show=snoozed) ---------- */

/**
 * Deliberately de-emphasised and SLIM: what it is, how it stood when parked, and a
 * link to the customer. No snooze/mark-done here — parked work gets its actions back
 * on the live card when the snooze ends. Nothing on this row can change state.
 */
function SnoozedRow({ row }: { row: SnoozedRowData }) {
  const href = customerHref(row.mobile);
  return (
    <li
      data-testid="worklist-snoozed-row"
      data-kind={row.kind}
      className="flex items-center gap-4 border-l-4 px-4 py-2 opacity-75 ![border-left-color:#cbd5e1] first:rounded-t-[7px] last:rounded-b-[7px] dark:![border-left-color:#334155]"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[13px] font-semibold leading-tight text-clinic-700 dark:text-gray-200">
            {row.name}
          </span>
          <span className="text-xs tabular-nums text-clinic-500 dark:text-gray-400">
            {row.mobile}
          </span>
          <span className="rounded bg-clinic-100 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-clinic-600 ring-1 ring-clinic-200 dark:bg-navy-700 dark:text-gray-300 dark:ring-navy-600">
            Snoozed
          </span>
        </div>
        <p className="mt-0.5 text-xs tabular-nums leading-snug text-clinic-500 dark:text-gray-400">
          {row.detail}
        </p>
      </div>
      {href && (
        <Link
          to={href}
          className="shrink-0 rounded text-xs font-medium text-clinic-500 hover:text-clinic-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2 dark:text-gray-400 dark:hover:text-white"
        >
          View customer
        </Link>
      )}
    </li>
  );
}

const chipBase =
  "touch-manipulation rounded-md px-2.5 py-1 text-xs font-medium ring-1 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-500 focus-visible:ring-offset-2";

function Chip({
  to,
  active,
  children,
}: {
  to: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      aria-current={active ? "true" : undefined}
      className={`${chipBase} ${
        active
          ? "bg-clinic-900 font-semibold text-white ring-clinic-900 dark:bg-white dark:text-navy-700 dark:ring-white"
          : "bg-white text-clinic-600 ring-clinic-300 hover:bg-clinic-100 hover:text-clinic-900 dark:!bg-navy-800 dark:text-gray-300 dark:ring-navy-700"
      }`}
    >
      {children}
    </Link>
  );
}

export default function Worklist() {
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchParams] = useSearchParams();

  /**
   * The Customer-360. `selected` is the row being inspected; `openerRef` is the DOM row
   * that opened it, so closing the panel puts focus back exactly where the CM left it —
   * they can keep triaging with ↑/↓ without ever touching the mouse.
   */
  const [selected, setSelected] = useState<WorklistItem | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const onOpen = useCallback((item: WorklistItem, opener: HTMLElement) => {
    openerRef.current = opener;
    setSelected(item);
  }, []);

  const onClosePanel = useCallback(() => {
    setSelected(null);
    // The row may have been re-rendered (or actioned out of existence) while the panel
    // was open — only restore focus if it is still on the page.
    const opener = openerRef.current;
    if (opener?.isConnected) opener.focus();
    openerRef.current = null;
  }, []);

  const showSnoozed = searchParams.get("show") === "snoozed";
  /**
   * Legacy deep-links from when retention was a lane here (`?tier=p2`) and when the
   * queue had an "urgent only" mode (`?filter=urgent`). Both filters are gone; rather
   * than silently ignore a bookmark and show something the CM didn't ask for, send
   * `tier=p2` to where that work actually lives now.
   */
  const legacyTier = searchParams.get("tier");

  const chats = useQuery({
    queryKey: ["worklist", "pending-chats"],
    queryFn: fetchPendingChats,
  });
  const appointments = useQuery({
    queryKey: ["worklist", "appointments"],
    queryFn: fetchAppointments,
  });
  const tasks = useQuery({
    queryKey: ["worklist", "expiry-tasks"],
    queryFn: fetchExpiryTasks,
  });
  // The top lane: parents waiting on a Babynama WhatsApp line right now. Read-only,
  // like the other queue fetches — its cards only LINK into waplify and copy text.
  const careTasks = useQuery({
    queryKey: ["worklist", "care-tasks"],
    queryFn: fetchCareTasks,
  });
  // The pending WhatsApp support chats that trigger the escalation calls — so the team
  // can see WHICH questions the phone is ringing about. Read-only, same as the others.
  const supportChats = useQuery({
    queryKey: ["worklist", "support-chats"],
    queryFn: fetchPendingSupportChats,
  });
  // The glance's question counts — the ONE read added over the queue's own fetches
  // (the same endpoint the retired Home read). Optional like the health pill: a
  // failure renders "—", never a fake zero, and never breaks the queue.
  const pendingCounts = useQuery({
    queryKey: ["worklist", "pending-counts"],
    queryFn: fetchPendingCounts,
    retry: false,
    refetchInterval: 60_000,
  });
  // Clinic-health signals (read/write bot up-down, doctor-reply watchdog, stuck-
  // undelivered answers). Optional: no retries, and a failure renders the strip as
  // nothing — it must never break the queue, and never claim health it can't verify.
  const clinicHealth = useQuery({
    queryKey: ["worklist", "clinic-health"],
    queryFn: fetchClinicHealth,
    retry: false,
    refetchInterval: 60_000,
  });
  // Every waplify session's live connection state — read/write/doctor/care-manager
  // numbers — so a down bot is visible at a glance. Optional, same rules as above.
  const waSessions = useQuery({
    queryKey: ["worklist", "wa-sessions"],
    queryFn: fetchWaSessions,
    retry: false,
    refetchInterval: 60_000,
  });
  // The Pulse's "answered recently" read. Optional exactly like the counts/health reads:
  // no retries, and a failure surfaces as the panel's empty state — never breaks the queue.
  const recentlyAnswered = useQuery({
    queryKey: ["worklist", "recently-answered"],
    queryFn: fetchRecentlyAnswered,
    retry: false,
    refetchInterval: 60_000,
  });

  const action = useMutation<ActionResult, unknown, ActionVars>({
    mutationFn: (vars: ActionVars): Promise<ActionResult> => {
      if (vars.type === "mark-done") return markTaskDone(vars.item.sourceId);
      if (vars.item.kind === "chat")
        return snoozeConversation(vars.item.sourceId, vars.duration);
      if (vars.item.kind === "appointment")
        return snoozeAppointment(
          vars.item.sourceId,
          vars.duration,
          vars.item.snoozeId
        );
      return snoozeTask(vars.item.sourceId, vars.duration);
    },
    onMutate: (vars: ActionVars) => {
      setErrors((prev) => {
        if (!(vars.item.key in prev)) return prev;
        const next = { ...prev };
        delete next[vars.item.key];
        return next;
      });
    },
    onSuccess: () => {
      // Refetch both queues — the actioned row drops out server-side.
      void queryClient.invalidateQueries({ queryKey: ["worklist"] });
      // The actioned customer just left the queue; a 360 still hovering over them is
      // stale. Close it and put the CM back on the list, ready for the next row.
      setSelected(null);
      const opener = openerRef.current;
      if (opener?.isConnected) opener.focus();
      openerRef.current = null;
    },
    onError: (err: unknown, vars: ActionVars) => {
      setErrors((prev) => ({ ...prev, [vars.item.key]: errorMessage(err) }));
    },
  });

  const pendingKey =
    action.isPending && action.variables ? action.variables.item.key : null;

  const onMarkDone = useCallback(
    (item: WorklistItem) => action.mutate({ type: "mark-done", item }),
    [action]
  );
  const onSnooze = useCallback(
    (item: WorklistItem, duration: SnoozeDuration) =>
      action.mutate({ type: "snooze", item, duration }),
    [action]
  );

  /**
   * Dismiss a "Parents waiting" card — mirror of the P2 mark-done mutation, but a care
   * card can back MORE THAN ONE picard task (same parent, two Babynama lines), so it
   * closes every `taskId` before refetching. Read-only in spirit: `markTaskDone` only
   * flips Task.status; nothing here messages the parent.
   */
  const careDismiss = useMutation<
    TaskStatusResponse[],
    unknown,
    CareWaitingItem
  >({
    mutationFn: (item: CareWaitingItem): Promise<TaskStatusResponse[]> =>
      Promise.all(item.taskIds.map((id) => markTaskDone(id))),
    onMutate: (item: CareWaitingItem) => {
      setErrors((prev) => {
        if (!(item.key in prev)) return prev;
        const next = { ...prev };
        delete next[item.key];
        return next;
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["worklist"] });
    },
    onError: (err: unknown, item: CareWaitingItem) => {
      setErrors((prev) => ({ ...prev, [item.key]: errorMessage(err) }));
    },
  });

  const careDismissKey =
    careDismiss.isPending && careDismiss.variables
      ? careDismiss.variables.key
      : null;

  const onCareDismiss = useCallback(
    (item: CareWaitingItem) => careDismiss.mutate(item),
    [careDismiss]
  );

  /**
   * Close an escalating WhatsApp chat that isn't a question — community-activity chatter
   * that re-armed the SLA on a chat some earlier real question had made doctor-assigned.
   * Only stops the clock: picard refuses anything not on this board, and nothing is sent
   * to the parent. Errors key off the chat id so they render on the row that failed.
   */
  const closeChat = useMutation<
    CloseSupportChatResponse,
    unknown,
    PendingSupportChat
  >({
    mutationFn: (chat: PendingSupportChat) => closeSupportChat(chat.chat_id),
    onMutate: (chat: PendingSupportChat) => {
      setErrors((prev) => {
        const key = supportChatKey(chat.chat_id);
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["worklist"] });
    },
    onError: (err: unknown, chat: PendingSupportChat) => {
      setErrors((prev) => ({
        ...prev,
        [supportChatKey(chat.chat_id)]: errorMessage(err),
      }));
    },
  });

  const closingChatId =
    closeChat.isPending && closeChat.variables
      ? closeChat.variables.chat_id
      : null;

  const onCloseSupportChat = useCallback(
    (chat: PendingSupportChat) => closeChat.mutate(chat),
    [closeChat]
  );

  /**
   * P0 = breached questions AND imminent/overdue appointments, interleaved by lateness
   * (see priority.ts). Both are service failures a customer is feeling right now, so
   * they are ranked against each other rather than parked in separate lists.
   */
  // fetchAppointments returns picard's raw custom_filter rows; the live lane is
  // `is_actionable && !is_snoozed` (unchanged), the snoozed remainder stays visible
  // in the glance and the ?show=snoozed view instead of silently vanishing.
  const liveAppointments = useMemo(
    () =>
      (appointments.data ?? []).filter((a) => a.is_actionable && !a.is_snoozed),
    [appointments.data]
  );
  const snoozedAppointments = useMemo(
    () => (appointments.data ?? []).filter((a) => a.is_snoozed),
    [appointments.data]
  );

  const p0 = useMemo(() => {
    const now = Date.now();
    return [
      ...buildP0(chats.data ?? [], now),
      ...buildAppointments(liveAppointments, now),
    ].sort((a, b) => b.rank - a.rank);
  }, [chats.data, liveAppointments]);
  const p2 = useMemo(() => buildP2(tasks.data ?? [], Date.now()), [tasks.data]);
  // Retention no longer renders as a lane here — it collapses to a summary strip that
  // hands off to the /retention tab. `inPipeline` = customers a CM has already engaged
  // (any recorded CRM stage); it is the strip's third figure.
  const inPipeline = useMemo(() => {
    const now = Date.now();
    // Derive from the SAME projection as `atRisk` (buildP2): a task rescheduled into the
    // future isn't in the at-risk list, so counting it as "in pipeline" made the strip's
    // three figures disagree with each other.
    return (tasks.data ?? []).filter((t) => {
      if (t.scheduled_at) {
        const due = Date.parse(t.scheduled_at);
        if (!Number.isNaN(due) && due > now) return false;
      }
      return !!t.crm?.stage;
    }).length;
  }, [tasks.data]);
  // Top lane — longest-waiting parent first, medical outranking non-medical at an equal
  // wait (see buildCareWaiting). Wait is picard's flag snapshot, not the wall clock.
  const careWaiting = useMemo(
    () => buildCareWaiting(careTasks.data ?? []),
    [careTasks.data]
  );

  // The summary always describes the WHOLE queue, never the filtered view — a filter
  // must not be able to hide how much work is actually outstanding.
  const summary = useMemo(() => summarize(p0, p2), [p0, p2]);
  const pendingSupportChats = supportChats.data ?? [];
  // The lamp counts ALL act-now work: SLA-P0 breaches, parents waiting on WhatsApp, and
  // the pending support chats that are actively escalating (ringing the hotline).
  const actNow = p0.length + careWaiting.length + pendingSupportChats.length;

  /* ----- the operational glance: always-on numbers, meaningful at zero ----- */
  const questionGlance = useMemo(
    () =>
      pendingCounts.data ? summarizeQuestionCounts(pendingCounts.data) : null,
    [pendingCounts.data]
  );
  const snoozedRows = useMemo(
    () =>
      buildSnoozedRows(
        (chats.data ?? []).filter((c) => c.is_snoozed),
        snoozedAppointments,
        Date.now()
      ),
    [chats.data, snoozedAppointments]
  );

  /**
   * The PAST SLA tile's sub-line. The lane deliberately merges questions and
   * appointments (both are service failures a customer feels now), so the tile counts
   * the lane and the sub-line says what it's made of — instead of a second tile
   * un-saying the merge.
   */
  const slaBreakdown = useMemo(() => {
    const questions = p0.filter((i) => i.kind === "chat").length;
    const appts = p0.filter((i) => i.kind === "appointment").length;
    /**
     * The old Home's best idea, restored: pair the FIRE with the BACKLOG ("2 of 6
     * questions"), so the cell says both how much is breaching and how much is out
     * there. Paired ONLY for questions — pendingCounts totals conversations, while
     * picard's appointment feed returns just the actionable set, so "of N" next to a
     * number that includes appointments would silently mix two scopes (exactly the
     * disagreement that made the old `questions past SLA` tile untrustworthy).
     */
    const q =
      questionGlance !== null
        ? `${questions} of ${questionGlance.pending} question${
            questionGlance.pending === 1 ? "" : "s"
          }`
        : `${questions} question${questions === 1 ? "" : "s"}`;
    const parts = [q, `${appts} appt${appts === 1 ? "" : "s"}`];
    if (snoozedRows.length > 0) parts.push(`${snoozedRows.length} snoozed`);
    return parts.join(" · ");
  }, [p0, snoozedRows, questionGlance]);

  /** WORST WAIT's sub — who it is. The one glance figure that says how BAD, not how many. */
  const worstWaiter = useMemo(() => {
    const worst = p0.reduce<WorklistItem | null>(
      (acc, i) => ((i.waitingMins ?? 0) > (acc?.waitingMins ?? -1) ? i : acc),
      null
    );
    return worst && (worst.waitingMins ?? 0) > 0 ? worst.name : null;
  }, [p0]);

  const snoozedHref = (show: boolean) =>
    show ? "/worklist?show=snoozed" : "/worklist";

  const loading = chats.isLoading || tasks.isLoading || appointments.isLoading;
  const health = clinicHealth.data ?? null;
  const sessions = waSessions.data ?? null;
  const infraAlert = healthHasAlert(sessions, health);

  // Honour the old `?tier=p2` bookmark: that work moved to its own tab.
  if (legacyTier === "p2") return <Navigate to="/retention" replace />;

  return (
    <div className="mx-auto max-w-6xl px-6 pt-5 pb-12">
      <header className="mb-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-clinic-400 dark:text-gray-500">
          Babynama Clinic-OS
        </p>
        <h1 className="mt-0.5 text-[22px] font-bold leading-tight tracking-tight text-clinic-900 [text-wrap:balance] dark:text-white">
          Worklist
        </h1>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="text-sm text-clinic-500 dark:text-gray-400">
            One prioritized queue — service SLA first, retention second.
          </p>
          {/*
            Healthy infra costs one muted pill. It only becomes a full-width red banner
            (below) when something is actually broken — so red on this page always means
            "act", and a working bot never outranks a waiting parent.
          */}
          {!infraAlert && <ConnectionsPill sessions={sessions} />}
        </div>

        {/* Broken infra preempts everything: renders ONLY on a real alert. */}
        <ClinicHealthStrip sessions={sessions} health={health} />

        {/*
          The glance is the TABLE OF CONTENTS for the queue below: every cell is one
          segment's count, the cells are disjoint, and the lamp is exactly their sum —
          so "4 on fire = 1 escalating + 0 waiting + 3 past SLA" reconciles on sight.
          Cells link to their segment. Only real breach figures take crit colour.
        */}
        <div
          data-testid="worklist-summary"
          aria-live="polite"
          className="mt-4 flex flex-wrap items-stretch divide-x divide-clinic-100 rounded-lg border border-clinic-200 bg-white shadow-sm dark:divide-navy-700 dark:border-navy-700 dark:!bg-navy-800"
        >
          {loading ? (
            <span className="px-5 py-4 text-sm text-clinic-500 dark:text-gray-400">
              Loading…
            </span>
          ) : (
            <>
              {/*
                The lamp: the 2-second answer to "is anything on fire?". It mirrors
                the P0 tier exactly — red with the count while act-now work exists,
                a calm green ALL CLEAR when it doesn't. Horizon's opaque green-700
                (NOT green-50, a 10%-alpha wash) — the one non-crit/warn colour on
                the page, reserved for this single "no fires" verdict.
              */}
              <div
                data-testid="glance-status"
                className={`flex min-w-[104px] flex-col justify-center rounded-l-[7px] px-5 py-3 ${
                  actNow > 0 ? "bg-crit-600" : "bg-green-700"
                }`}
              >
                <div className="text-[22px] font-bold tabular-nums leading-none text-white">
                  {actNow > 0 ? actNow : "✓"}
                </div>
                <div className="mt-1.5 whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-white/90">
                  {actNow > 0 ? "act now" : "all clear"}
                </div>
              </div>
              {/*
                Cells 2-4 DECOMPOSE the lamp — one per P0 segment, disjoint, summing to
                it. Each links to its segment. (The old strip re-sliced the same cards
                three ways — "questions past SLA" read a different source and visibly
                disagreed with the lane, "appts need action" un-said the lane's own
                question+appt merge, "expire today" duplicated the retention strip 40px
                below, and "need you this shift" was an unfalsifiable union of all three.)
              */}
              <Stat
                testId="glance-escalating"
                href="#seg-escalating"
                value={String(pendingSupportChats.length)}
                label="escalating · WA groups"
                tone={pendingSupportChats.length > 0 ? "critical" : "default"}
                sub="rings hotline at 25 min"
              />
              <Stat
                testId="glance-waiting"
                href="#seg-waiting"
                value={String(careWaiting.length)}
                label="parents waiting"
                tone={careWaiting.length > 0 ? "critical" : "default"}
                sub="on Babynama DMs"
              />
              <Stat
                testId="glance-pastsla"
                href="#seg-sla"
                value={String(p0.length)}
                label="past SLA"
                tone={p0.length > 0 ? "critical" : "default"}
                sub={slaBreakdown}
              />
              {/* The only cell that says how BAD, not how many. */}
              <Stat
                testId="glance-worst-wait"
                href="#seg-sla"
                value={
                  summary.oldestWaitMins > 0
                    ? formatWaiting(summary.oldestWaitMins)
                    : "—"
                }
                label="worst wait"
                tone={summary.oldestWaitMins > 0 ? "critical" : "default"}
                sub={worstWaiter ?? undefined}
              />
            </>
          )}
        </div>

        {/*
          "Urgent only" is GONE with the tile it mirrored: it filtered on the same
          unfalsifiable `urgent` union, and only hid the bottom of an already
          worst-first list. A filter nobody can define is a mode, not a feature — and
          with it goes "All work", which only existed to undo it.
        */}
        <nav
          aria-label="Worklist views"
          className="mt-3 flex flex-wrap items-center gap-2"
        >
          {/*
            Snoozed work is parked, not gone. The chip only appears when something IS
            snoozed (or the view is already open, so it can be closed) — a permanent
            "0 snoozed" would be noise.
          */}
          {(snoozedRows.length > 0 || showSnoozed) && (
            <Chip to={snoozedHref(!showSnoozed)} active={showSnoozed}>
              {snoozedRows.length} snoozed
            </Chip>
          )}
          <span className="ml-auto hidden text-xs text-clinic-400 dark:text-gray-500 sm:block">
            <kbd className="rounded border border-clinic-300 bg-clinic-100 px-1 font-mono text-[10px] text-clinic-600 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300">
              ↑
            </kbd>{" "}
            <kbd className="rounded border border-clinic-300 bg-clinic-100 px-1 font-mono text-[10px] text-clinic-600 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300">
              ↓
            </kbd>{" "}
            to move ·{" "}
            <kbd className="rounded border border-clinic-300 bg-clinic-100 px-1 font-mono text-[10px] text-clinic-600 dark:border-navy-600 dark:bg-navy-800 dark:text-gray-300">
              Enter
            </kbd>{" "}
            to open
          </span>
        </nav>
      </header>

      {/*
        ONE P0 PANEL, three segments. These are one queue in a CM's head — three
        separate bordered panels meant three ACT NOW tags and three red badges shouting
        at once, so red stopped meaning anything. One border, one alarm, one count that
        the glance decomposes. Segments keep their own order and their own card actions.
      */}
      <TierSection
        id="tier-p0"
        label="P0 · Act now"
        blurb="Everything a customer is feeling right now: escalating WhatsApp questions, parents waiting on our DMs, and anything past its answer-time SLA."
        count={actNow}
        accent="critical"
      >
        {/* --- escalating: what the hotline is ringing about --- */}
        <Segment
          id="seg-escalating"
          testId="seg-escalating"
          label="Escalating — WA groups"
          count={pendingSupportChats.length}
          quiet="nothing pending in the doctor groups"
        />
        {supportChats.isLoading && <LoadingTier />}
        {!supportChats.isLoading && supportChats.isError && (
          <ErrorTier onRetry={() => void supportChats.refetch()} />
        )}
        {!supportChats.isLoading &&
          !supportChats.isError &&
          pendingSupportChats.map((chat) => (
            <SupportChatCard
              key={chat.chat_id}
              chat={chat}
              onClose={onCloseSupportChat}
              pending={closingChatId === chat.chat_id}
              error={errors[supportChatKey(chat.chat_id)] ?? null}
            />
          ))}

        {/* --- parents waiting on our DMs --- */}
        <Segment
          id="seg-waiting"
          testId="seg-waiting"
          label="Waiting — DMs"
          count={careWaiting.length}
          quiet="no parents waiting on a Babynama line"
        />
        {careTasks.isLoading && <LoadingTier />}
        {!careTasks.isLoading && careTasks.isError && (
          <ErrorTier onRetry={() => void careTasks.refetch()} />
        )}
        {!careTasks.isLoading &&
          !careTasks.isError &&
          careWaiting.map((item) => (
            <CareWaitingCard
              key={item.key}
              item={item}
              onDismiss={onCareDismiss}
              pending={careDismissKey === item.key}
              error={errors[item.key] ?? null}
            />
          ))}

        {/* --- service SLA: in-app questions + appointments (deliberately merged) --- */}
        <Segment
          id="seg-sla"
          testId="seg-sla"
          label="Service SLA — questions + appts"
          count={p0.length}
          // WHY it is quiet, with the live numbers — not a blank reassurance.
          quiet={describeQuietP0(questionGlance, snoozedAppointments.length)}
        />
        {(chats.isLoading || appointments.isLoading) && <LoadingTier />}
        {!(chats.isLoading || appointments.isLoading) &&
          chats.isError &&
          appointments.isError && (
            <ErrorTier
              onRetry={() => {
                void chats.refetch();
                void appointments.refetch();
              }}
            />
          )}
        {!(chats.isLoading || appointments.isLoading) &&
          !(chats.isError && appointments.isError) &&
          p0.map((item, index) => (
            <WorklistCard
              key={item.key}
              item={item}
              onOpen={onOpen}
              onSnooze={onSnooze}
              pending={pendingKey === item.key}
              error={errors[item.key] ?? null}
              spotlight={index === 0}
            />
          ))}
      </TierSection>

      {/*
        The ?show=snoozed view: parked work, de-emphasised, BELOW the live queue.
        Sits between the tiers because everything snoozable today is P0-shaped
        (questions + appointments); P2 tasks leave via their own reschedule.
      */}
      {showSnoozed && (
        <TierSection
          id="tier-snoozed"
          label="Snoozed · returns when due"
          blurb="Parked deliberately — hidden from the live queue until each snooze ends. Read-only here."
          count={snoozedRows.length}
          accent="muted"
        >
          {snoozedRows.length === 0 && (
            <Notice>Nothing is snoozed right now.</Notice>
          )}
          {snoozedRows.map((row) => (
            <SnoozedRow key={row.key} row={row} />
          ))}
        </TierSection>
      )}

      {/*
        Retention is a GLANCE here, not a queue — one honest line ("retention second"),
        with the working surface (drafts, stage buttons, the whole pipeline) on the
        /retention tab. It used to render every at-risk customer as a card and bury the
        P0 fire queue above under dozens of P2 rows.
      */}
      <RetentionStrip
        atRisk={p2.length}
        expiringToday={summary.expiringToday}
        inPipeline={inPipeline}
      />

      {/*
        The Pulse — always-on situational awareness (what's pending, what doctors just
        answered) so the landing is never a blank "all clear". Display-only.
      */}
      <Pulse
        counts={pendingCounts.data ?? null}
        countsLoading={pendingCounts.isLoading}
        answered={recentlyAnswered.data ?? []}
        answeredLoading={recentlyAnswered.isLoading}
        answeredError={recentlyAnswered.isError}
      />

      {/*
        The Customer-360. Keyed by the row so switching customers remounts it — a fresh
        fetch, a fresh focus trap, no stale profile bleeding between customers.
        Mark done is P2-only, exactly as on the row.
      */}
      {selected && (
        <Customer360
          key={selected.key}
          item={selected}
          onClose={onClosePanel}
          onMarkDone={selected.tier === "p2" ? onMarkDone : undefined}
          onSnooze={onSnooze}
          pending={pendingKey === selected.key}
          error={errors[selected.key] ?? null}
        />
      )}
    </div>
  );
}
